import React, { useState } from "react";
import {
  Crown, Shield, Briefcase, Code2, Headphones, Calculator, PenTool, Eye,
  Check, Minus, Lock, Monitor, Smartphone, ShieldCheck, ShieldAlert,
  MoreHorizontal, Pencil, UserCog, Target, RotateCcw, KeyRound, Mail, Trash2,
  Unlock, MessageSquare, LogIn, LogOut, UserPlus, ShieldQuestion, FileSearch,
  AlertTriangle, Building2,
} from "lucide-react";

/* ---------- Accent maps ---------- */
export const ACCENT = {
  lime: { text: "text-[#A3E635]", badge: "bg-[#A3E635]/12 text-[#A3E635]", dot: "bg-[#A3E635]", bar: "bg-[#A3E635]", ring: "ring-[#A3E635]/50" },
  sky: { text: "text-sky-300", badge: "bg-sky-500/12 text-sky-300", dot: "bg-sky-400", bar: "bg-sky-400", ring: "ring-sky-400/50" },
  amber: { text: "text-amber-400", badge: "bg-amber-500/12 text-amber-300", dot: "bg-amber-400", bar: "bg-amber-400", ring: "ring-amber-400/50" },
  red: { text: "text-red-400", badge: "bg-red-500/12 text-red-300", dot: "bg-red-500", bar: "bg-red-500", ring: "ring-red-500/50" },
  violet: { text: "text-violet-300", badge: "bg-violet-500/12 text-violet-300", dot: "bg-violet-400", bar: "bg-violet-400", ring: "ring-violet-400/50" },
  cyan: { text: "text-cyan-300", badge: "bg-cyan-500/12 text-cyan-300", dot: "bg-cyan-400", bar: "bg-cyan-400", ring: "ring-cyan-400/50" },
  emerald: { text: "text-emerald-300", badge: "bg-emerald-500/12 text-emerald-300", dot: "bg-emerald-400", bar: "bg-emerald-400", ring: "ring-emerald-400/50" },
  fuchsia: { text: "text-fuchsia-300", badge: "bg-fuchsia-500/12 text-fuchsia-300", dot: "bg-fuchsia-400", bar: "bg-fuchsia-400", ring: "ring-fuchsia-400/50" },
  gray: { text: "text-gray-200", badge: "bg-white/5 text-gray-400", dot: "bg-gray-500", bar: "bg-gray-600", ring: "ring-white/15" },
};
export const A = (k) => ACCENT[k] || ACCENT.gray;

/* ---------- Initials / Avatar ---------- */
export const initialsOf = (name) =>
  (name || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";

export function avatarRing(u) {
  if (!u) return "gray";
  if (u.role === "super_admin") return "lime";
  if (u.role === "team_member") return ROLE_ACCENT[u.team_role] || "violet";
  if (u.role === "client") return "sky";
  return "gray";
}

export function Avatar({ name, accent = "gray", size = "md", testid }) {
  const sz = size === "sm" ? "w-7 h-7 text-[10px]" : size === "lg" ? "w-11 h-11 text-sm" : "w-9 h-9 text-xs";
  return (
    <div className={`${sz} rounded-full bg-white/[0.06] ring-2 ${A(accent).ring} flex items-center justify-center font-bold text-gray-100 shrink-0`} data-testid={testid}>
      {initialsOf(name)}
    </div>
  );
}

/* ---------- Role icon / accent ---------- */
export const ROLE_ACCENT = {
  super_admin: "lime", admin_ops: "violet", projektleitung: "sky", entwickler: "cyan",
  support: "amber", buchhaltung: "emerald", content_seo: "fuchsia", viewer: "gray",
};
const ROLE_ICON = {
  super_admin: Crown, admin_ops: Shield, projektleitung: Briefcase, entwickler: Code2,
  support: Headphones, buchhaltung: Calculator, content_seo: PenTool, viewer: Eye,
};
export function RoleIcon({ roleKey, className = "w-4 h-4" }) {
  const Icon = ROLE_ICON[roleKey] || Eye;
  return <Icon className={`${className} ${A(ROLE_ACCENT[roleKey] || "gray").text}`} />;
}

/* ---------- Stat cards ---------- */
export function StatCard({ icon: Icon, label, value, sub, accent = "gray", testid }) {
  const a = A(accent);
  return (
    <div className="bg-[#15151b] border border-white/[0.07] rounded-2xl p-4 flex items-center gap-3.5 hover:border-white/15 transition-colors" data-testid={testid}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${a.badge}`}>{Icon && <Icon className="w-5 h-5" />}</div>
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500 truncate">{label}</div>
        <div className={`text-2xl font-black leading-tight ${a.text}`}>{value}</div>
        {sub != null && sub !== "" ? <div className="text-[11px] text-gray-500 truncate">{sub}</div> : null}
      </div>
    </div>
  );
}

export function ProgressStat({ icon: Icon, label, value, pct, sub, accent = "gray", testid }) {
  const a = A(accent);
  const width = Math.max(0, Math.min(100, pct || 0));
  return (
    <div className="bg-[#15151b] border border-white/[0.07] rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${a.badge}`}>{Icon && <Icon className="w-4 h-4" />}</div>
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">{label}</span>
      </div>
      <div className="flex items-end gap-2">
        <span className={`text-3xl font-black leading-none ${a.text}`}>{value}</span>
        {pct != null && <span className="text-xs font-semibold text-gray-500 mb-0.5">{width}%</span>}
      </div>
      {sub ? <div className="text-[11px] text-gray-500 mt-1.5">{sub}</div> : null}
      {pct != null && (
        <div className="mt-3 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
          <div className={`h-full rounded-full ${a.bar} transition-all`} style={{ width: `${width}%` }} />
        </div>
      )}
    </div>
  );
}

/* ---------- Device cell ---------- */
export function DeviceCell({ device, t, className = "" }) {
  if (!device) return <span className={`text-gray-600 text-xs ${className}`}>{t("Nicht erfasst")}</span>;
  const low = device.toLowerCase();
  const Icon = (low.includes("ios") || low.includes("android") || low.includes("iphone")) ? Smartphone : Monitor;
  return (
    <span className={`inline-flex items-center gap-1.5 text-gray-400 text-xs ${className}`}>
      <Icon className="w-3.5 h-3.5 text-gray-500 shrink-0" /> {device.replace(" · ", " / ")}
    </span>
  );
}

/* ---------- Matrix access cell ---------- */
export const ACCESS_META = {
  full: { icon: Check, cls: "text-[#A3E635]", label: "Vollzugriff" },
  read: { icon: Eye, cls: "text-sky-300", label: "Nur lesen" },
  limited: { icon: null, cls: "text-amber-400", label: "Eingeschränkt" },
  none: { icon: Minus, cls: "text-gray-600", label: "Kein Zugriff" },
  forbidden: { icon: Lock, cls: "text-red-400", label: "Nicht erlaubt" },
};
export function AccessCell({ level, t }) {
  const m = ACCESS_META[level] || ACCESS_META.none;
  const Icon = m.icon;
  if (level === "read" || level === "forbidden") {
    return <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${m.cls}`} title={t(m.label)}>{Icon && <Icon className="w-3.5 h-3.5" />} {t(m.label)}</span>;
  }
  if (level === "limited") return <span className={`text-lg leading-none ${m.cls}`} title={t(m.label)}>•</span>;
  return Icon ? <Icon className={`w-4 h-4 mx-auto ${m.cls}`} /> : <span className={m.cls}>—</span>;
}

/* ---------- Icon button (with tooltip) ---------- */
export function IconBtn({ icon: Icon, label, onClick, disabled, tone = "default", testid }) {
  const toneCls = tone === "red"
    ? "text-gray-400 hover:text-red-300 hover:bg-red-500/10"
    : "text-gray-400 hover:text-white hover:bg-white/10";
  return (
    <button
      onClick={onClick} disabled={disabled} data-testid={testid} title={label} aria-label={label}
      className={`p-1.5 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${disabled ? "text-gray-600" : toneCls}`}>
      <Icon className="w-4 h-4" />
    </button>
  );
}

/* ---------- Inline row actions (icons + overflow) ---------- */
export function InlineActions({ u, kind, isOwner, t, busy, menu, setMenu, actions, nav }) {
  if (busy === u.id) {
    return <div className="flex justify-end pr-2"><RotateCcw className="w-4 h-4 animate-spin text-gray-400" /></div>;
  }
  const locked = u.active === false || u.locked;
  return (
    <div className="flex items-center justify-end gap-0.5 relative">
      <IconBtn icon={Eye} label={t("Ansehen / Bearbeiten")} onClick={() => actions.edit(u)} testid={`action-view-${u.id}`} />
      <IconBtn icon={Pencil} label={t("Bearbeiten")} onClick={() => actions.edit(u)} testid={`action-edit-${u.id}`} />
      {kind === "team" && !isOwner && <IconBtn icon={UserCog} label={t("Team-Rolle ändern")} onClick={() => actions.teamRole(u)} testid={`action-teamrole-${u.id}`} />}
      {kind === "team" && !isOwner && <IconBtn icon={Target} label={t("Kunden zuweisen")} onClick={() => actions.scope(u)} testid={`action-scope-${u.id}`} />}
      {kind === "client" && !isOwner && <IconBtn icon={UserCog} label={t("Kontotyp ändern")} onClick={() => actions.role(u)} testid={`action-role-${u.id}`} />}
      <IconBtn icon={RotateCcw} label={t("2FA zurücksetzen")} onClick={() => actions.reset2fa(u)} testid={`action-reset-2fa-${u.id}`} />
      {!isOwner && (locked
        ? <IconBtn icon={Unlock} label={t("Konto entsperren")} onClick={() => actions.unlock(u)} testid={`action-unlock-${u.id}`} />
        : <IconBtn icon={Lock} label={t("Konto sperren")} onClick={() => actions.lock(u)} tone="red" testid={`action-lock-${u.id}`} />)}
      {kind === "client" && <IconBtn icon={MessageSquare} label={t("Support öffnen")} onClick={() => nav("/admin/support")} testid={`action-support-${u.id}`} />}
      {isOwner && <span className="inline-flex items-center gap-1 text-[10px] text-[#A3E635] font-semibold pl-1 pr-1.5 whitespace-nowrap" data-testid={`owner-protected-${u.id}`}><Crown className="w-3 h-3" /> {t("Geschützt")}</span>}

      <button onClick={() => setMenu(menu === u.id ? null : u.id)} data-testid={`user-actions-${u.id}`} className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"><MoreHorizontal className="w-4 h-4" /></button>
      {menu === u.id && (
        <>
          <button className="fixed inset-0 z-30 cursor-default" onClick={() => setMenu(null)} aria-label="close" />
          <div className="absolute right-0 top-9 z-40 w-56 bg-[#16161c] border border-white/10 rounded-xl shadow-2xl py-1.5 text-sm" data-testid={`user-menu-${u.id}`}>
            <MenuRow icon={KeyRound} label={t("Passwort zurücksetzen")} onClick={() => actions.resetPw(u)} testid={`action-reset-pw-${u.id}`} />
            <MenuRow icon={Mail} label={t("Einladung erneut senden")} onClick={() => actions.resend(u)} testid={`action-resend-${u.id}`} />
            {kind === "client" && !isOwner && <MenuRow icon={ShieldQuestion} label={t("Kontotyp ändern")} onClick={() => actions.role(u)} testid={`action-role-menu-${u.id}`} />}
            {isOwner
              ? <div className="px-3 py-2 text-[11px] text-[#A3E635] flex items-center gap-1.5"><Crown className="w-3.5 h-3.5" /> {t("Owner — geschützt")}</div>
              : <><div className="h-px bg-white/10 my-1" /><MenuRow icon={Trash2} label={t("Löschen")} onClick={() => actions.delete(u)} testid={`action-delete-${u.id}`} tone="red" /></>}
          </div>
        </>
      )}
    </div>
  );
}
function MenuRow({ icon: Icon, label, onClick, testid, tone }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-white/5 transition-colors ${tone === "red" ? "text-red-300" : "text-gray-200"}`}>
      <Icon className="w-4 h-4 shrink-0" /> {label}
    </button>
  );
}

/* ---------- Account status helper ---------- */
export function accountStatus(u, t) {
  if (u.prepared) return { key: "vorbereitet", c: "amber", dot: "bg-sky-400", txt: "text-sky-300", label: t("Vorbereitet") };
  if (u.active === false) return { key: "deaktiviert", c: "red", dot: "bg-red-500", txt: "text-red-400", label: t("Deaktiviert") };
  if (u.locked) return { key: "gesperrt", c: "red", dot: "bg-red-500", txt: "text-red-400", label: t("Gesperrt") };
  if (!u.last_login) return { key: "einladung", c: "amber", dot: "bg-amber-400", txt: "text-amber-400", label: t("Einladung offen") };
  return { key: "aktiv", c: "green", dot: "bg-[#A3E635]", txt: "text-[#A3E635]", label: t("Aktiv") };
}

/* ---------- 2FA cell ---------- */
export function TwoFACell({ on, t }) {
  return on
    ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#A3E635]"><ShieldCheck className="w-3.5 h-3.5" /> {t("Aktiv")}</span>
    : <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-400"><ShieldAlert className="w-3.5 h-3.5" /> {t("Offen")}</span>;
}

/* ---------- Audit event meta (icon + category + severity) ---------- */
export const EVENT_ICON = {
  login_erfolg: LogIn, login_fehlgeschlagen: AlertTriangle, login_gesperrt: Lock, "2fa_gesperrt": Lock,
  konto_gesperrt: Lock, konto_entsperrt: Unlock, sperre_aufgehoben: Unlock,
  rolle_geaendert: UserCog, team_rolle_geaendert: UserCog, kunden_zuweisung_geaendert: Target,
  einladung_gesendet: Mail, benutzer_angelegt: UserPlus, passwort_zurueckgesetzt: KeyRound,
  "2fa_aktiviert": ShieldCheck, "2fa_zurueckgesetzt": RotateCcw, "2fa_wiederhergestellt": RotateCcw,
  "2fa_deaktiviert": ShieldAlert, login_2fa_offen: Shield, login_2fa_fehlgeschlagen: AlertTriangle,
};
export function eventIcon(ev) { return EVENT_ICON[ev] || FileSearch; }

const LOCK_EVENTS = new Set(["login_gesperrt", "2fa_gesperrt", "konto_gesperrt"]);
export function auditStatus(e, t) {
  if (e.success === false) {
    if (LOCK_EVENTS.has(e.event)) return { label: t("Blockiert"), cls: "bg-red-500/12 border-red-500/30 text-red-300", dot: "bg-red-500" };
    return { label: t("Fehlgeschlagen"), cls: "bg-red-500/12 border-red-500/30 text-red-300", dot: "bg-red-500" };
  }
  if (e.event === "login_erfolg") return { label: t("Erfolgreich"), cls: "bg-[#A3E635]/12 border-[#A3E635]/30 text-[#A3E635]", dot: "bg-[#A3E635]" };
  return { label: t("Ausgeführt"), cls: "bg-[#A3E635]/12 border-[#A3E635]/30 text-[#A3E635]", dot: "bg-[#A3E635]" };
}
export function eventSeverity(e, t, CRITICAL) {
  if (e.success === false && LOCK_EVENTS.has(e.event)) return { label: t("Hoch"), cls: "text-red-400", dot: "bg-red-500" };
  if (e.success === false) return { label: t("Warnung"), cls: "text-amber-400", dot: "bg-amber-400" };
  if (CRITICAL.has(e.event)) return { label: t("Mittel"), cls: "text-sky-300", dot: "bg-sky-400" };
  return { label: t("Niedrig"), cls: "text-[#A3E635]", dot: "bg-[#A3E635]" };
}

/* ---------- Country flag emoji ---------- */
export function countryFlag(cc) {
  if (!cc || cc.length !== 2) return "";
  try { return String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)); }
  catch { return ""; }
}

/* ---------- Country flag emoji END ---------- */
/* ---------- Small section header for cards ---------- */
export function CardHead({ icon: Icon, title, count, right, testid }) {
  return (
    <div className="px-5 py-3.5 border-b border-white/10 flex items-center justify-between gap-2" data-testid={testid}>
      <div className="font-bold tracking-tight text-sm flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-[#A3E635]" />} {title}
        {count != null && <span className="text-gray-500 font-normal">({count})</span>}
      </div>
      {right}
    </div>
  );
}

/* ---------- Sectioned drawer rows ---------- */
export function DrawerSection({ title, children }) {
  return (
    <div className="mb-4">
      <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-500 mb-2">{title}</div>
      <div className="rounded-xl border border-white/[0.07] bg-black/20 px-3 divide-y divide-white/[0.05]">{children}</div>
    </div>
  );
}
export function KV({ label, value, mono, accent }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <span className="text-xs text-gray-500 shrink-0">{label}</span>
      <span className={`text-xs text-right break-all ${accent || "text-gray-200"} ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}
